import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../services/modal.service';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { I18nTranslator } from '../../i18n/translate';
import { Farm } from '../../models/farm.model';
import { Organisation } from '../../models/organisation.model';
import { Page } from '../../models/page.model';
import { User } from '../../models/user.model';
import { FarmService } from '../../services/farm.service';
import { FarmerAuthorizationsService } from '../../services/farmer-authorizations.service';
import { OrganisationService } from '../../services/organisation.service';
import { KeycloakService } from '../../services/keycloak.service';
import { UserService } from '../../services/user.service';
import { showErrorMessage, toNull } from '../../utils/utils';
import { DEFAULT_ITEMS_PER_PAGE } from '../../utils/constants';
import { FarmEditComponent } from './farm-edit.component';
import { AutocompleteComponent } from '../../components/autocomplete/autocomplete.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginationModule } from 'ngx-bootstrap/pagination';

@Component({
  standalone: true,
  selector: 'farms-cmp',
  templateUrl: 'farms.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    FarmEditComponent,
    TranslatePipe,
    CommonModule,
    FormsModule,
    PaginationModule,
    AutocompleteComponent,
  ],
  providers: [FarmService, UserService, OrganisationService],
})
export class FarmComponent implements OnInit, AfterViewInit, OnDestroy {
  farms: Farm[];

  error: any;

  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;
  @ViewChild('termOwnerFilter') termOwnerFilter: ElementRef;

  public rows: Array<any> = [];

  public page: number = 1;
  public itemsPerPage: number = DEFAULT_ITEMS_PER_PAGE;
  public maxSize: number = 5;
  public numPages: number = 1;
  public length: number = 0;
  protected isAdmin = false;

  public filter: string;
  public filterOrga: string;
  // Farm picked in the farm name suggestions: filters on its id instead of the text
  public filterFarmId: number = null;
  public organisations: Organisation[] = [];
  public advisorFilter: string = null;
  public ownerFilter: string = null;

  public showRef: boolean;
  public isFarmer: boolean;
  public isNegoce: boolean;
  public isManager: boolean;
  // Loaded async from block 6 (C2027-196); defaults to true so the button does not flicker.
  public canCreateFarm = true;

  static filterToApply: string;
  static filterOrgaToApply: string;
  static filterFarmIdToApply: number;
  static filterAdvisorToApply: string;
  static filterOwnerToApply: string;
  public advisors: Array<User> = [];

  // Free text typed in the autocompletes, debounced before filtering
  private farmTextChanges = new Subject<string>();
  private orgaTextChanges = new Subject<string>();
  private destroyed = new Subject<void>();

  readonly farmLabel = (farm: Farm) => farm.name;
  readonly farmDetail = (farm: Farm) => [farm.postalCode, farm.city].filter(Boolean).join(' ');
  readonly organisationLabel = (organisation: Organisation) => organisation.name;
  readonly searchFarms = (term: string): Observable<Farm[]> =>
    this.farmService
      .getFarmsAdvanced(1, 20, 'name', 'asc', { filter: term }, null, null, null)
      .pipe(map((page) => page.content));

  constructor(
    private farmService: FarmService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private modal: ModalService,
    private userService: UserService,
    private organisationService: OrganisationService,
    private farmerAuthorizationsService: FarmerAuthorizationsService,
  ) {
    this.isFarmer = KeycloakService.auth.authz.hasRealmRole('ROLE_FARMER');
    this.isManager = KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.showRef =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.farmerAuthorizationsService.can('createFarm').subscribe((allowed) => (this.canCreateFarm = allowed));
  }

  getFarmsPaginated(
    pageIndex: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: any,
    filterOrga: string,
    farmIds: number[] = null,
  ) {
    this.farmService
      .getFarmsAdvanced(
        pageIndex,
        this.itemsPerPage,
        sortColumnName,
        sortColumnOrder,
        filter,
        filterOrga,
        farmIds,
        null,
      )
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<Farm>) {
    this.rows = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  addFarm() {
    this.router.navigate(['/farms', 'new']);
  }

  deleteFarm(farm: Farm, event: any) {
    event.stopPropagation();
    this.modal.confirm(this.i18nTranslator.translate('farm.table.delete.confirm', farm.name)).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.farmService.delete(farm).subscribe(
        (res) => {
          this.onChangeTable();
        },
        (error) => this.handleDeleteError(error),
      );
    });
  }

  editFarm(farm: Farm, event: any) {
    this.router.navigate(['/farms', 'edit', farm.id]);
  }

  ngOnInit() {
    // Restored before the first check: they are bound to the autocompletes' [text]
    this.filter = FarmComponent.filterToApply;
    this.filterFarmId = FarmComponent.filterFarmIdToApply || null;
    this.filterOrga = FarmComponent.filterOrgaToApply;

    this.farmTextChanges.pipe(debounceTime(500), takeUntil(this.destroyed)).subscribe((text) => {
      this.filter = text;
      this.filterFarmId = null;
      this.updateFilters();
    });
    this.orgaTextChanges.pipe(debounceTime(500), takeUntil(this.destroyed)).subscribe((text) => {
      this.filterOrga = text;
      this.updateOrgaFilters();
    });
    if (this.isAdmin || this.isNegoce) {
      this.organisationService.getOrganisations().subscribe((organisations) => {
        this.organisations = [...organisations].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  onFarmTextChange(text: string) {
    this.farmTextChanges.next(text);
  }

  onFarmSelected(farm: Farm) {
    this.filter = farm.name;
    this.filterFarmId = farm.id;
    this.updateFilters();
  }

  onOrganisationTextChange(text: string) {
    this.orgaTextChanges.next(text);
  }

  onOrganisationSelected(organisation: Organisation) {
    // The farms endpoint filters organisations by name only
    this.filterOrga = organisation.name;
    this.updateOrgaFilters();
  }

  updateUIWithFilters() {
    if (FarmComponent.filterAdvisorToApply && this.termAdvisorFilter) {
      this.advisorFilter = FarmComponent.filterAdvisorToApply;
      this.termAdvisorFilter.nativeElement.value = this.advisorFilter;
    }
    if (FarmComponent.filterOwnerToApply && this.termOwnerFilter) {
      this.ownerFilter = FarmComponent.filterOwnerToApply;
      this.termOwnerFilter.nativeElement.value = this.ownerFilter;
    }
  }

  ngAfterViewInit() {
    if (this.isAdmin || this.isNegoce || this.isManager) {
      fromEvent(this.termAdvisorFilter.nativeElement, 'keyup')
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term: any) => {
          this.advisorFilter = <string>term.target.value;
          this.updateFilters();
        });
    }
    if (!this.isFarmer) {
      fromEvent(this.termOwnerFilter.nativeElement, 'keyup')
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term: any) => {
          this.ownerFilter = <string>term.target.value;
          this.updateFilters();
        });
    }
    this.onChangeTable();
    this.updateUIWithFilters();
  }

  updateFilters() {
    FarmComponent.filterToApply = this.filter;
    FarmComponent.filterFarmIdToApply = this.filterFarmId;
    if (this.isAdmin || this.isNegoce || this.isManager) {
      FarmComponent.filterAdvisorToApply = this.advisorFilter;
    }
    if (!this.isFarmer) {
      FarmComponent.filterOwnerToApply = this.ownerFilter;
    }
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  updateOrgaFilters() {
    FarmComponent.filterOrgaToApply = this.filterOrga;
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    const farmId = FarmComponent.filterFarmIdToApply;
    const filters = {
      // A picked farm filters on its id; typed text on the name
      filter: farmId ? null : FarmComponent.filterToApply,
      filterAdvisor: FarmComponent.filterAdvisorToApply,
      filterOwner: FarmComponent.filterOwnerToApply,
    };
    this.getFarmsPaginated(
      pageNumber,
      'id',
      'desc',
      filters,
      FarmComponent.filterOrgaToApply,
      farmId ? [farmId] : null,
    );
  }

  handleDeleteError(error: any) {
    // HttpClient puts the parsed response envelope in error.error
    const backendMessage = error?.error?.errorMessage;
    if (error?.status === 400 && backendMessage) {
      showErrorMessage(this.modal, backendMessage);
    } else {
      showErrorMessage(this.modal, this.i18nTranslator.translate('farm.table.delete.error'));
    }
  }
}
